/**
 * Custom Hook: useDocumentHead
 *
 * Keeps SEO-critical <head> tags in sync with the active language and page,
 * since this is a client-rendered single-page app. Updates:
 *   - document.title
 *   - <meta name="description">
 *   - <html lang="..">
 *   - <link rel="canonical">
 *
 * Metadata is localized so search engines and social crawlers see the right
 * language when a visitor shares a page.
 */
import { useEffect } from 'react';
import { PAGE_STATES } from '../constants/appConfig';

const SITE_URL = 'https://cropshield.tech/';

// Localized, page-aware title + description.
const META = {
  en: {
    home: {
      title: 'CropShield | Solar-Powered Crop Protection & Pest Control Device',
      description:
        'CropShield is a solar-powered automatic insect control device with 400-600m coverage, UV LED technology and mobile app monitoring — chemical-free crop protection that cuts pesticide costs and improves yield.',
    },
    preset: {
      title: 'Crop Presets & Recommended Settings | CropShield',
      description:
        'Browse crop-specific presets and recommended CropShield settings to protect your crop from its active pests without chemical pesticides.',
    },
  },
  hi: {
    home: {
      title: 'CropShield | सौर-संचालित फसल सुरक्षा और कीट नियंत्रण उपकरण',
      description:
        'CropShield एक सौर-संचालित स्वचालित कीट नियंत्रण उपकरण है — 400-600 मीटर कवरेज, UV LED तकनीक और मोबाइल ऐप निगरानी के साथ रसायन-मुक्त फसल सुरक्षा।',
    },
    preset: {
      title: 'फसल प्रीसेट और अनुशंसित सेटिंग | CropShield',
      description:
        'अपनी फसल को रसायनों के बिना उसके सक्रिय कीटों से बचाने के लिए फसल-विशिष्ट प्रीसेट और अनुशंसित CropShield सेटिंग देखें।',
    },
  },
  gu: {
    home: {
      title: 'CropShield | સૌર-સંચાલિત પાક સુરક્ષા અને જંતુ નિયંત્રણ ઉપકરણ',
      description:
        'CropShield એ સૌર-સંચાલિત સ્વચાલિત જંતુ નિયંત્રણ ઉપકરણ છે — 400-600 મીટર કવરેજ, UV LED ટેકનોલોજી અને મોબાઇલ એપ મોનિટરિંગ સાથે રસાયણ-મુક્ત પાક સુરક્ષા.',
    },
    preset: {
      title: 'પાક પ્રીસેટ અને ભલામણ કરેલ સેટિંગ | CropShield',
      description:
        'તમારા પાકને રસાયણ વિના તેના સક્રિય જંતુઓથી બચાવવા માટે પાક-વિશિષ્ટ પ્રીસેટ અને ભલામણ કરેલ CropShield સેટિંગ બ્રાઉઝ કરો.',
    },
  },
};

const setMeta = (name, content) => {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const setProperty = (property, content) => {
  const tag = document.querySelector(`meta[property="${property}"]`);
  if (tag) tag.setAttribute('content', content);
};

export const useDocumentHead = (language = 'en', currentPage = PAGE_STATES.HOME) => {
  useEffect(() => {
    const lang = META[language] ? language : 'en';
    const pageKey = currentPage === PAGE_STATES.PRESET ? 'preset' : 'home';
    const meta = META[lang][pageKey];

    document.documentElement.lang = lang;
    document.title = meta.title;
    setMeta('description', meta.description);

    // Keep social preview tags aligned with the visible language/page.
    setProperty('og:title', meta.title);
    setProperty('og:description', meta.description);
    setProperty('og:locale', lang === 'en' ? 'en_US' : lang === 'hi' ? 'hi_IN' : 'gu_IN');

    // Canonical stays on the primary URL for the home page; preset is a hash view.
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', SITE_URL);
  }, [language, currentPage]);
};
